import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Megaphone, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * InspectorAnnouncementsModal
 * Angela Feb 2026 v2 — PRD Inspector App / Item 1
 *
 * Bottom-sheet modal that BLOCKS the inspector's My Orders screen until
 * they explicitly confirm every active admin message. Two-step gate:
 *   1) Tick the "Li e entendi" checkbox (initially disabled button).
 *   2) Only then does the "Concordo, continuar" button enable.
 *
 * Multiple active messages are stacked into ONE sheet (scroll list).
 * Confirming acknowledges every message in a single call so the modal
 * disappears at once. Not dismissible any other way — no ESC, no
 * backdrop tap, no close button.
 */
const InspectorAnnouncementsModal = ({ onAllConfirmed }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inspector/announcements/pending`);
      setItems(r.data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    if (!acknowledged) return;
    setError("");
    setSubmitting(true);
    try {
      const ids = items.map(i => i.id);
      await axios.post(`${API}/inspector/announcements/confirm`, { ids });
      setItems([]);
      if (typeof onAllConfirmed === "function") onAllConfirmed();
    } catch (e) {
      setError(e.response?.data?.detail || "Não foi possível confirmar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-[#0B1F3A]/85 backdrop-blur-sm"
      data-testid="inspector-announcements-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Bottom sheet on mobile, centered on desktop */}
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden border-t-4 border-[#D1A054] max-h-[92vh] flex flex-col animate-[slideUp_0.25s_ease-out]">
        <style>{`@keyframes slideUp { from { transform: translateY(100%); opacity: 0.5; } to { transform: translateY(0); opacity: 1; } }`}</style>

        {/* Grabber (bottom-sheet visual cue) */}
        <div className="pt-2 pb-1 flex justify-center sm:hidden">
          <div className="w-10 h-1.5 rounded-full bg-gray-300"></div>
        </div>

        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-br from-[#2F5D8A] to-[#1F3F60] text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#D1A054]/25">
              <Megaphone className="h-5 w-5 text-[#D1A054]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[#D1A054] font-bold">Importante</p>
              <h2 className="text-base font-bold" data-testid="announcements-modal-title">
                {items.length === 1 ? "Recado da administração" : `${items.length} recados da administração`}
              </h2>
            </div>
          </div>
        </div>

        {/* Body — scrollable message list */}
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100" data-testid="announcements-modal-body">
          {items.map((a, idx) => (
            <div key={a.id} className="px-6 py-4" data-testid={`announcement-item-${a.id}`}>
              {items.length > 1 && (
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#D1A054] text-white text-[11px] font-bold">{idx + 1}</span>
                  <span className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Mensagem {idx + 1}</span>
                </div>
              )}
              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed" data-testid={`announcement-body-${a.id}`}>{a.message}</p>
            </div>
          ))}
        </div>

        {/* Footer — gate: checkbox + button */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 space-y-3">
          {error && <p className="text-xs text-red-600" data-testid="announcements-modal-error">{error}</p>}
          <label className="flex items-start gap-2.5 cursor-pointer group">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#D1A054] cursor-pointer flex-shrink-0"
              data-testid="announcements-ack-checkbox"
            />
            <span className="text-sm text-gray-700 select-none">
              <span className="font-semibold">Li e entendi</span>{items.length > 1 ? " todos os recados acima." : " o recado acima."}
            </span>
          </label>
          <button
            onClick={confirm}
            disabled={!acknowledged || submitting}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-lg transition-colors ${
              acknowledged
                ? "bg-[#D1A054] hover:bg-[#B98F42] text-white"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
            data-testid="announcements-accept-all-btn"
          >
            <Check className="h-4 w-4" />
            {submitting ? "Confirmando…" : "Concordo, continuar"}
          </button>
          <p className="text-[10px] text-gray-400 text-center">
            Você precisa concordar para acessar suas ordens.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InspectorAnnouncementsModal;
